import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Product, CartItem, PaymentMethod, Transaction, ProductCategory, HeldTransaction, PaymentSplit, StockAlert, Supplier, GRN, Expense, PriceTierKey, StockBatch, SalesUnit, VolumePricing, QuickButton, Cheque, ChequeStatus } from '@/types/pos'
import { apiService } from '@/services/api'
import { liveSyncService } from '@/services/liveSyncService'
import { socketService } from '@/services/socketService'

// Raw (un-expanded) product docs cache — loaded via REST, patched by realtime socket events,
// then re-expanded (variant cards) for the store. Replaces the old Firestore onSnapshot snapshot.
let _rawProducts: any[] = []

/**
 * Get the effective unit price for a cart item based on its selected price tier.
 * Falls back to retail (product.price) if no tier selected or tier has no value.
 */
export const getEffectivePrice = (item: CartItem): number => {
  // If there's an explicit override, use it
  if (item.unitPriceOverride !== undefined && item.unitPriceOverride > 0) {
    return item.unitPriceOverride
  }

  const { product, selectedPriceTier } = item

  switch (selectedPriceTier) {
    case 'wholesale':
      return product.wholesalePrice || product.price
    case 'discount':
      return product.discountPrice || product.price
    case 'marked':
      return product.markedPrice || product.price
    case 'cost':
      return product.cost || product.price
    case 'retail':
    case undefined:
      return product.price
    default: {
      // Check custom price tiers
      const customTier = product.priceTiers?.find(t => t.name === selectedPriceTier)
      return customTier?.price || product.price
    }
  }
}

/**
 * Get the default price tier for a product based on the selected customer type.
 * - Wholesale customers automatically get wholesale pricing
 * - Saved regular customers get discount/promo pricing if available
 * - Guest/walk-in customers always get retail price (no automatic discounts)
 */
export const getDefaultPriceTier = (product: Product, customerType?: 'wholesale' | 'regular' | 'special' | null): PriceTierKey => {
  if (customerType === 'wholesale' && product.wholesalePrice && product.wholesalePrice > 0) {
    return 'wholesale'
  }
  if ((customerType === 'regular' || customerType === 'special') && product.discountPrice && product.discountPrice > 0) {
    return 'discount'
  }
  return 'retail'
}

/**
 * Resolve the correct price from a stock batch based on customer type.
 * Batch carries its own wholesalePrice / discountPrice set at GRN time.
 * Falls back to batch.sellingPrice (retail) when the tier price is unavailable.
 */
export const getBatchPriceForCustomer = (
  batch: StockBatch,
  customerType?: 'wholesale' | 'regular' | 'special' | null
): number => {
  if (customerType === 'wholesale' && batch.wholesalePrice && batch.wholesalePrice > 0) {
    return batch.wholesalePrice
  }
  if ((customerType === 'regular' || customerType === 'special') && batch.discountPrice && batch.discountPrice > 0) {
    return batch.discountPrice
  }
  return batch.sellingPrice
}

/**
 * Get available price tiers for a product (only tiers that have values set).
 * Tiers shown at POS for switching: Retail, Discount (promo), Wholesale.
 * Marked price is display-only (manufacturer price), not selectable at POS.
 */
export const getAvailablePriceTiers = (product: Product): Array<{ key: PriceTierKey; label: string; price: number }> => {
  const tiers: Array<{ key: PriceTierKey; label: string; price: number }> = [
    { key: 'retail', label: 'Retail', price: product.price }
  ]

  if (product.discountPrice && product.discountPrice > 0) {
    tiers.push({ key: 'discount', label: 'Promo', price: product.discountPrice })
  }
  if (product.wholesalePrice && product.wholesalePrice > 0) {
    tiers.push({ key: 'wholesale', label: 'Wholesale', price: product.wholesalePrice })
  }

  // Custom tiers
  if (product.priceTiers?.length) {
    for (const tier of product.priceTiers) {
      if (tier.price > 0) {
        tiers.push({ key: tier.name, label: tier.name, price: tier.price })
      }
    }
  }

  return tiers
}

/**
 * Calculate line total for a quantity using volume (block) pricing when defined.
 * Base price = effective unit price for the line; remainder uses base price.
 */
export const calculateVolumeLineTotal = (
  quantity: number,
  basePrice: number,
  volumePricing?: VolumePricing | null
): number => {
  if (!quantity || quantity <= 0) return 0
  if (!volumePricing?.rules?.length) return Math.round(basePrice * quantity * 100) / 100
  const rules = [...volumePricing.rules].sort((a, b) => b.blockSize - a.blockSize)
  let remainingQty = quantity
  let lineTotal = 0
  for (const rule of rules) {
    if (rule.blockSize < 1 || rule.pricePerUnit < 0) continue
    const blocks = Math.floor(remainingQty / rule.blockSize)
    lineTotal += blocks * rule.blockSize * rule.pricePerUnit
    remainingQty -= blocks * rule.blockSize
  }
  lineTotal += remainingQty * basePrice
  return Math.round(lineTotal * 100) / 100
}

/**
 * Get line total for a cart item (volume-aware, before item discount %).
 */
export const getLineTotalForItem = (item: CartItem): number => {
  const basePrice = getEffectivePrice(item)
  const rawTotal = calculateVolumeLineTotal(
    item.quantity,
    basePrice,
    item.product.volumePricing
  )
  const itemDisc = (item.discount || 0) / 100
  return Math.round(rawTotal * (1 - itemDisc) * 100) / 100
}

/**
 * Maps a raw Firestore/API product document to the POS Product shape and expands
 * products with salesUnits into separate virtual product cards (one per sales unit).
 * This runs inside both the Firestore onSnapshot callback and the barcode lookup.
 */
const buildExpandedProducts = (rawDocs: any[]): Product[] => {
  const mappedProducts: Product[] = rawDocs.map((p: any) => ({
    id: p.id || p._id,
    _id: p._id || p.id,
    name: p.title || p.name || 'Untitled',
    description: p.description || '',
    sku: p.sku || p.barcode || `SKU-${p.id || p._id}`,
    barcode: p.barcode || p.isbn || p.sku,
    isbn: p.isbn || p.barcode,
    price: (p.salePrice != null && p.salePrice !== '') ? Number(p.salePrice) : (p.price != null && p.price !== '') ? Number(p.price) : 0,
    cost: (p.originalPrice != null && p.originalPrice !== '') ? Number(p.originalPrice) : (p.cost != null && p.cost !== '') ? Number(p.cost) : 0,
    wholesalePrice: p.wholesalePrice != null ? Number(p.wholesalePrice) : undefined,
    discountPrice: p.discountPrice != null ? Number(p.discountPrice) : undefined,
    markedPrice: p.markedPrice != null ? Number(p.markedPrice) : undefined,
    priceTiers: p.priceTiers || undefined,
    volumePricing: p.volumePricing || undefined,
    category: typeof p.category === 'string'
      ? { id: p.category, name: p.category, isActive: true, createdAt: new Date(), updatedAt: new Date() }
      : p.category || { id: 'general', name: 'General', isActive: true, createdAt: new Date(), updatedAt: new Date() },
    supplier: p.supplier || undefined,
    supplierId: p.supplierId || undefined,
    stock: p.stock ?? 0,
    minStock: p.minStock ?? 5,
    maxStock: p.maxStock ?? 100,
    reorderLevel: p.reorderLevel || undefined,
    reorderQty: p.reorderQty || undefined,
    expiryDate: p.expiryDate || undefined,
    unit: p.unit || 'pcs',
    baseUnit: p.baseUnit || undefined,
    salesUnits: p.salesUnits || undefined,
    variantGroupId: p.variantGroupId || undefined,
    variantGroupName: p.variantGroupName || undefined,
    image: p.coverImage || p.image || '📖',
    isActive: p.isActive ?? true,
    tags: p.tags || [],
    createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
    updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
  }))

  const expandedProducts: Product[] = []
  for (const p of mappedProducts) {
    if (p.salesUnits && p.salesUnits.length > 0) {
      // Keep master product for Inventory/GRN (tagged so POS can filter it out)
      ;(p as any)._isMaster = true
      expandedProducts.push(p)
      // Virtual cards for POS terminal (one per sales unit)
      for (const su of p.salesUnits) {
        expandedProducts.push({
          ...p,
          id: `${p.id}__${su.id}`,
          _id: `${p._id || p.id}__${su.id}`,
          name: `${p.name} - ${su.name}`,
          price: su.price,
          wholesalePrice: su.wholesalePrice != null ? su.wholesalePrice : p.wholesalePrice,
          discountPrice: su.discountPrice != null ? su.discountPrice : p.discountPrice,
          stock: su.conversionFactor > 0 ? Math.floor(p.stock / su.conversionFactor) : p.stock,
          _masterProductId: p.id,
          _salesUnit: su,
          _isMaster: undefined,
        } as any)
      }
    } else {
      expandedProducts.push(p)
    }
  }

  // Build synthetic group cards for variant groups — one card per group for POS grid
  // Tag individual variant members (and their UoM cards) so POS hides them
  const variantGroupMap = new Map<string, { name: string; members: Product[] }>()
  for (const p of expandedProducts) {
    if (p.variantGroupId) {
      if (!variantGroupMap.has(p.variantGroupId)) {
        variantGroupMap.set(p.variantGroupId, { name: p.variantGroupName || 'Variant', members: [] })
      }
      // Collect master products only (not UoM virtual cards) as group members for the picker
      if (!(p as any)._salesUnit && !(p as any)._isMaster) {
        variantGroupMap.get(p.variantGroupId)!.members.push(p)
      } else if ((p as any)._isMaster) {
        // Master products with UoM are also variant members (for the picker)
        variantGroupMap.get(p.variantGroupId)!.members.push(p)
      }
      // Tag ALL products in variant groups (masters, UoM cards, regular) as hidden from POS grid
      ;(p as any)._isVariantMember = true
    }
  }

  // Create one synthetic group card per variant group
  for (const [groupId, group] of variantGroupMap) {
    if (group.members.length === 0) continue
    const totalStock = group.members.reduce((sum, m) => sum + (m.stock || 0), 0)
    const firstMember = group.members[0]
    const groupCard: any = {
      id: `__vg_${groupId}`,
      _id: `__vg_${groupId}`,
      name: group.name,
      description: `${group.members.length} variant${group.members.length !== 1 ? 's' : ''}`,
      sku: '',
      barcode: '',
      price: 0,
      cost: 0,
      category: firstMember.category,
      stock: totalStock,
      minStock: 0,
      maxStock: 0,
      unit: firstMember.baseUnit || firstMember.unit || 'pcs',
      image: '',  // Layers icon will be shown by POS
      isActive: true,
      tags: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      // Variant group metadata
      variantGroupId: groupId,
      variantGroupName: group.name,
      _isVariantGroupCard: true,
    }
    expandedProducts.push(groupCard)
  }

  return expandedProducts
}

const mapCategory = (c: any): ProductCategory => ({
  id: c.id || c._id,
  _id: c._id || c.id,
  name: c.name || 'Unknown',
  icon: c.icon || '📚',
  description: c.description || '',
  parentId: c.parentId,
  order: c.order != null ? c.order : undefined,
  isActive: c.isActive ?? true,
  createdAt: c.createdAt ? new Date(c.createdAt) : new Date(),
  updatedAt: c.updatedAt ? new Date(c.updatedAt) : new Date(),
})

interface POSState {
  cart: CartItem[]
  selectedCustomer: string | null
  selectedCustomerName: string | null
  paymentMethod: PaymentMethod
  paymentSplits: PaymentSplit[]
  discount: number
  discountType: 'percentage' | 'fixed'
  tax: number
  subtotal: number
  total: number
  isOffline: boolean
  products: Product[]
  categories: ProductCategory[]
  customers: any[]
  suppliers: Supplier[]
  grns: GRN[]
  expenses: Expense[]
  cheques: Cheque[]
  batches: StockBatch[]
  pendingBatchSelection: { product: Product; batches: StockBatch[] } | null
  pendingVariantSelection: { groupId: string; groupName: string; variants: Product[] } | null
  isLoading: boolean
  pendingTransactions: Transaction[]
  heldTransactions: HeldTransaction[]
  lastTransaction: Transaction | null
  stockAlerts: StockAlert[]
  /** Advance payments recorded (customer pays before delivery); applied at checkout. */
  advancePayments: Array<{
    id: string
    requestId: string
    customerId: string | null
    amount: number
    appliedAmount: number
    totalAtRequest: number
    createdAt: string
  }>
  quickButtons: QuickButton[]
  /** Internal — Firestore onSnapshot unsubscribe functions. NOT persisted. */
  _unsubscribers: (() => void)[]

  // Real-time listener lifecycle
  initRealtimeListeners: () => void
  cleanupListeners: () => void

  // Actions
  fetchProducts: () => Promise<void>
  fetchCategories: () => Promise<void>
  fetchProductByBarcode: (barcode: string) => Promise<Product | null>
  addToCart: (product: Product, quantity?: number, priceTier?: PriceTierKey) => void
  removeFromCart: (productId: string, batchId?: string) => void
  updateCartItem: (productId: string, quantity: number, batchId?: string) => void
  setCartItemPriceTier: (productId: string, priceTier: PriceTierKey, batchId?: string) => void
  setCartItemDiscount: (productId: string, discountPercent: number, batchId?: string) => void
  clearCart: () => void
  setSelectedCustomer: (customerId: string | null, customerName?: string | null) => void
  setPaymentMethod: (method: PaymentMethod) => void
  setPaymentSplits: (splits: PaymentSplit[]) => void
  setDiscount: (discount: number, type?: 'percentage' | 'fixed') => void
  setTax: (tax: number) => void
  calculateTotals: () => void
  processTransaction: (amountPaid?: number, change?: number, advanceReceiptsToApply?: Array<{ requestId: string; amount: number }>) => Promise<Transaction>
  fetchCustomers: () => Promise<void>
  addCustomer: (customer: any) => Promise<void>
  updateCustomer: (id: string, updates: any) => Promise<void>
  deleteCustomer: (id: string) => Promise<void>
  fetchSuppliers: () => Promise<void>
  addSupplier: (supplier: Partial<Supplier>) => Promise<void>
  updateSupplier: (id: string, updates: Partial<Supplier>) => Promise<void>
  deleteSupplier: (id: string) => Promise<void>
  fetchGRNs: () => Promise<void>
  createGRN: (grn: Partial<GRN>) => Promise<GRN>
  updateGRN: (id: string, updates: Partial<GRN>) => Promise<void>
  deleteGRN: (id: string) => Promise<void>
  fetchExpenses: () => Promise<void>
  addExpense: (expense: Partial<Expense>) => Promise<void>
  updateExpense: (id: string, updates: Partial<Expense>) => Promise<void>
  deleteExpense: (id: string) => Promise<void>
  fetchCheques: () => Promise<void>
  addCheque: (cheque: Partial<Cheque>) => Promise<void>
  updateCheque: (id: string, updates: Partial<Cheque>) => Promise<void>
  deleteCheque: (id: string) => Promise<void>
  updateChequeStatus: (id: string, status: ChequeStatus) => Promise<void>
  fetchBatches: (productId?: string) => Promise<void>
  fetchActiveBatchesForProduct: (productId: string) => Promise<StockBatch[]>
  addBatch: (batch: Partial<StockBatch>) => Promise<void>
  updateBatch: (id: string, updates: Partial<StockBatch>) => Promise<void>
  deleteBatch: (id: string) => Promise<void>
  confirmBatchSelection: (product: Product, batch: StockBatch) => void
  cancelBatchSelection: () => void
  confirmVariantSelection: (selections: { product: Product; quantity: number }[]) => void
  cancelVariantSelection: () => void
  addReturnToCart: (item: {
    product: Product
    quantity: number
    unitPrice: number
    transactionId: string
    receiptNumber: string
    batchId?: string
    batchLabel?: string
  }) => void
  addPendingTransaction: (transaction: Transaction) => void
  syncPendingTransactions: () => Promise<void>
  setOfflineMode: (isOffline: boolean) => void

  // Hold Transaction Actions
  holdTransaction: (name: string, notes?: string) => void
  resumeTransaction: (heldTransaction: HeldTransaction) => void
  deleteHeldTransaction: (transactionId: string) => void

  // Stock Alert Actions
  checkStockAlerts: () => void
  dismissStockAlerts: () => void

  // Advance payment (by receipt ID, not tied to customer)
  recordAdvancePayment: (params: { requestId: string; amount: number; totalAtRequest: number; customerPhone?: string; customerName?: string; customerId?: string | null; items?: Array<{ productName: string; quantity: number; price: number }> }) => Promise<void>
  getAdvanceByRequestId: (requestId: string) => { requestId: string; amount: number; appliedAmount: number } | null
  getUnappliedAmountForRequestId: (requestId: string) => number
  applyAdvanceByRequestIds: (entries: Array<{ requestId: string; amount: number }>) => void

  // Wallet Actions
  topUpCustomerWallet: (customerId: string, amount: number, note?: string) => Promise<void>
  // NOTE: wallet deduction at checkout is handled server-side in pos-transactions.js, not here

  // Quick Button Actions
  addQuickButton: (productId: string) => void
  removeQuickButton: (productId: string) => void

  // Inventory Actions
  addProduct: (product: Partial<Product>) => Promise<void>
  updateProduct: (id: string, updates: Partial<Product>) => Promise<void>
  deleteProduct: (id: string) => Promise<void>
  addCategory: (category: Partial<ProductCategory>) => Promise<void>
  updateCategory: (id: string, updates: Partial<ProductCategory>) => Promise<void>
  deleteCategory: (id: string) => Promise<void>
}

export const usePOSStore = create<POSState>()(
  persist(
    (set, get) => ({
      cart: [],
      selectedCustomer: null,
      selectedCustomerName: null,
      paymentMethod: 'cash',
      paymentSplits: [],
      discount: 0,
      discountType: 'percentage',
      tax: 0,
      subtotal: 0,
      total: 0,
      isOffline: false,
      products: [],
      categories: [],
      customers: [],
      suppliers: [],
      grns: [],
      expenses: [],
      cheques: [],
      batches: [],
      pendingBatchSelection: null,
      pendingVariantSelection: null,
      isLoading: false,
      pendingTransactions: [],
      heldTransactions: [],
      lastTransaction: null,
      stockAlerts: [],
      advancePayments: [],
      quickButtons: [],
      _unsubscribers: [],

      initRealtimeListeners: () => {
        // Cleanup any previous listeners before starting new ones
        get()._unsubscribers.forEach(fn => fn())

        // Initial load via REST (parity with the old Firestore onSnapshot initial sync)
        get().fetchProducts()
        get().fetchCustomers()
        get().fetchBatches()

        // Live updates via Socket.IO (replaces the browser->Firestore onSnapshot listeners)
        socketService.connect()

        let custTimer: any = null
        let batchTimer: any = null

        // products:changed -> patch the raw cache in-memory + re-expand (avoids re-downloading the list)
        const onProducts = (evt: any) => {
          if (!evt || !evt.id) return
          if (evt.operation === 'delete') {
            _rawProducts = _rawProducts.filter((p: any) => (p._id || p.id) !== evt.id)
          } else if (evt.doc) {
            const idx = _rawProducts.findIndex((p: any) => (p._id || p.id) === evt.id)
            if (idx >= 0) {
              const ex = _rawProducts[idx]
              // image/coverImage are projected out of change events to keep them small — keep the existing one
              _rawProducts[idx] = { ...ex, ...evt.doc, image: evt.doc.image ?? ex.image, coverImage: evt.doc.coverImage ?? ex.coverImage }
            } else {
              _rawProducts = [..._rawProducts, evt.doc]
            }
          }
          set({ products: buildExpandedProducts(_rawProducts) })
          liveSyncService.markFetched('products')
        }
        // customers/batches change frequently during a sale — debounce a cheap re-fetch
        const onCustomers = () => { clearTimeout(custTimer); custTimer = setTimeout(() => get().fetchCustomers(), 600) }
        const onBatches = () => { clearTimeout(batchTimer); batchTimer = setTimeout(() => get().fetchBatches(), 600) }

        socketService.on('products:changed', onProducts)
        socketService.on('customers:changed', onCustomers)
        socketService.on('batches:changed', onBatches)

        set({ _unsubscribers: [
          () => socketService.off('products:changed', onProducts),
          () => socketService.off('customers:changed', onCustomers),
          () => socketService.off('batches:changed', onBatches),
          () => { clearTimeout(custTimer); clearTimeout(batchTimer) },
        ] })
        console.log('[RT] Real-time listeners started via Socket.IO (products, customers, stock_batches)')
      },

      cleanupListeners: () => {
        get()._unsubscribers.forEach(fn => fn())
        set({ _unsubscribers: [] })
        console.log('[RT] Real-time listeners cleaned up')
      },

      // Load the full product list via REST, cache the raw docs, and expand variant cards.
      fetchProducts: async () => {
        try {
          const body: any = await apiService.get(`/products?limit=all&skipFilter=true&lean=1`)
          let raw: any[] = []
          if (Array.isArray(body)) raw = body
          else if (Array.isArray(body?.data)) raw = body.data
          else if (Array.isArray(body?.products)) raw = body.products
          else if (Array.isArray(body?.data?.products)) raw = body.data.products
          _rawProducts = raw
          set({ products: buildExpandedProducts(raw) })
          liveSyncService.markFetched('products')
        } catch (error) {
          console.error('[RT] fetchProducts failed:', error)
        }
      },

      fetchProductByBarcode: async (barcode: string) => {
        try {
          const response = await apiService.get<{ success: boolean; data: any }>(`/products/barcode/${barcode}`)
          if (response.success && response.data) {
            const p = response.data
            const product: Product = {
              id: p.id || p._id,
              _id: p._id || p.id,
              name: p.title || p.name || 'Untitled',
              description: p.description || '',
              sku: p.sku || p.barcode || `SKU-${p.id || p._id}`,
              barcode: p.barcode || p.isbn || p.sku,
              isbn: p.isbn || p.barcode,
              price: (p.salePrice != null && p.salePrice !== '') ? Number(p.salePrice) : (p.price != null && p.price !== '') ? Number(p.price) : 0,
              cost: (p.originalPrice != null && p.originalPrice !== '') ? Number(p.originalPrice) : (p.cost != null && p.cost !== '') ? Number(p.cost) : 0,
              wholesalePrice: p.wholesalePrice != null ? Number(p.wholesalePrice) : undefined,
              discountPrice: p.discountPrice != null ? Number(p.discountPrice) : undefined,
              markedPrice: p.markedPrice != null ? Number(p.markedPrice) : undefined,
              priceTiers: p.priceTiers || undefined,
              volumePricing: p.volumePricing || undefined,
              category: typeof p.category === 'string'
                ? { id: p.category, name: p.category, isActive: true, createdAt: new Date(), updatedAt: new Date() }
                : p.category || { id: 'general', name: 'General', isActive: true, createdAt: new Date(), updatedAt: new Date() },
              supplier: p.supplier || undefined,
              supplierId: p.supplierId || undefined,
              stock: p.stock ?? 0,
              minStock: p.minStock ?? 5,
              maxStock: p.maxStock ?? 100,
              reorderLevel: p.reorderLevel || undefined,
              reorderQty: p.reorderQty || undefined,
              expiryDate: p.expiryDate || undefined,
              unit: p.unit || 'pcs',
              image: p.coverImage || p.image || '📖',
              isActive: p.isActive ?? true,
              tags: p.tags || [],
              createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
              updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
            }
            return product
          }
          return null
        } catch (error) {
          console.error('Failed to fetch product by barcode:', error)
          return null
        }
      },

      fetchCategories: async () => {
        try {
          const response = await apiService.get<{ data: any[] }>('/categories')
          // Map backend category fields to POS ProductCategory format
          const mappedCategories: ProductCategory[] = response.data.map(mapCategory)
          // Sort categories alphabetically (A-Z) for consistent display
          mappedCategories.sort((a, b) => a.name.localeCompare(b.name))
          set({ categories: mappedCategories })
          liveSyncService.markFetched('categories')
        } catch (error) {
          console.error('Failed to fetch categories:', error)
        }
      },

      // Load customers via REST (kept live by Socket.IO customers:changed events).
      fetchCustomers: async () => {
        try {
          const body: any = await apiService.get(`/customers`)
          const list = Array.isArray(body) ? body : (body?.data || body?.customers || [])
          set({ customers: list })
          liveSyncService.markFetched('customers')
        } catch (error) {
          console.error('[RT] fetchCustomers failed:', error)
        }
      },

      addCustomer: async (customerData) => {
        set({ isLoading: true })
        try {
          const response = await apiService.post<{ success: boolean, data: any }>('/customers', customerData)
          if (response.success) {
            set((state) => ({ customers: [response.data, ...state.customers] }))
            liveSyncService.broadcast('customers', 'add')
          }
        } catch (error) {
          console.error('Failed to add customer:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateCustomer: async (id, updates) => {
        set({ isLoading: true })
        try {
          const response = await apiService.put<{ success: boolean, data: any }>(`/customers/${id}`, updates)
          if (response.success) {
            set((state) => ({
              customers: state.customers.map(c =>
                (c.id === id || c._id === id) ? response.data : c
              )
            }))
            liveSyncService.broadcast('customers', 'update')
          }
        } catch (error) {
          console.error('Failed to update customer:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      deleteCustomer: async (id) => {
        set({ isLoading: true })
        try {
          const response = await apiService.delete<{ success: boolean }>(`/customers/${id}`)
          if (response.success) {
            set((state) => ({
              customers: state.customers.filter(c =>
                (c.id !== id && c._id !== id)
              )
            }))
            liveSyncService.broadcast('customers', 'delete')
          }
        } catch (error) {
          console.error('Failed to delete customer:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      // Supplier CRUD
      fetchSuppliers: async () => {
        set({ isLoading: true })
        try {
          const response = await apiService.get<{ success: boolean, data: Supplier[] }>('/suppliers')
          if (response.success) {
            set({ suppliers: response.data })
            liveSyncService.markFetched('suppliers')
          }
        } catch (error) {
          console.error('Failed to fetch suppliers:', error)
        } finally {
          set({ isLoading: false })
        }
      },

      addSupplier: async (supplierData) => {
        set({ isLoading: true })
        try {
          const response = await apiService.post<{ success: boolean, data: Supplier }>('/suppliers', supplierData)
          if (response.success) {
            set((state) => ({ suppliers: [response.data, ...state.suppliers] }))
            liveSyncService.broadcast('suppliers', 'add')
          }
        } catch (error) {
          console.error('Failed to add supplier:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateSupplier: async (id, updates) => {
        set({ isLoading: true })
        try {
          const response = await apiService.put<{ success: boolean, data: Supplier }>(`/suppliers/${id}`, updates)
          if (response.success) {
            set((state) => ({
              suppliers: state.suppliers.map(s =>
                (s.id === id || s._id === id) ? { ...s, ...response.data } : s
              )
            }))
            liveSyncService.broadcast('suppliers', 'update')
          }
        } catch (error) {
          console.error('Failed to update supplier:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      deleteSupplier: async (id) => {
        set({ isLoading: true })
        try {
          const response = await apiService.delete<{ success: boolean }>(`/suppliers/${id}`)
          if (response.success) {
            set((state) => ({
              suppliers: state.suppliers.filter(s =>
                (s.id !== id && s._id !== id)
              )
            }))
            liveSyncService.broadcast('suppliers', 'delete')
          }
        } catch (error) {
          console.error('Failed to delete supplier:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      // GRN CRUD
      fetchGRNs: async () => {
        set({ isLoading: true })
        try {
          const response = await apiService.get<{ success: boolean, data: GRN[] }>('/grn')
          if (response.success) {
            set({ grns: response.data })
            liveSyncService.markFetched('grns')
          }
        } catch (error) {
          console.error('Failed to fetch GRNs:', error)
        } finally {
          set({ isLoading: false })
        }
      },

      createGRN: async (grnData) => {
        set({ isLoading: true })
        try {
          const response = await apiService.post<{ success: boolean, data: GRN }>('/grn', grnData, { timeout: 120000 })
          if (response.success) {
            set((state) => ({ grns: [response.data, ...state.grns] }))
            liveSyncService.broadcast('grns', 'add')
            // Products/batches updated by Firestore listener automatically
            return response.data
          }
          throw new Error('Failed to create GRN')
        } catch (error) {
          console.error('Failed to create GRN:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateGRN: async (id, updates) => {
        set({ isLoading: true })
        try {
          const response = await apiService.put<{ success: boolean, data: GRN }>(`/grn/${id}`, updates)
          if (response.success) {
            set((state) => ({
              grns: state.grns.map(g =>
                (g.id === id || g._id === id) ? { ...g, ...updates, ...response.data } : g
              )
            }))
            liveSyncService.broadcast('grns', 'update')
          }
        } catch (error) {
          console.error('Failed to update GRN:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      deleteGRN: async (id) => {
        set({ isLoading: true })
        try {
          const response = await apiService.delete<{ success: boolean }>(`/grn/${id}`)
          if (response.success) {
            set((state) => ({
              grns: state.grns.filter(g =>
                (g.id !== id && g._id !== id)
              )
            }))
            liveSyncService.broadcast('grns', 'delete')
          }
        } catch (error) {
          console.error('Failed to delete GRN:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      // Expense CRUD
      fetchExpenses: async () => {
        set({ isLoading: true })
        try {
          const response = await apiService.get<{ success: boolean, data: Expense[] }>('/expenses')
          if (response.success) {
            set({ expenses: response.data })
            liveSyncService.markFetched('expenses')
          }
        } catch (error) {
          console.error('Failed to fetch expenses:', error)
        } finally {
          set({ isLoading: false })
        }
      },

      addExpense: async (expenseData) => {
        set({ isLoading: true })
        try {
          const response = await apiService.post<{ success: boolean, data: Expense }>('/expenses', expenseData)
          if (response.success) {
            set((state) => ({ expenses: [response.data, ...state.expenses] }))
            liveSyncService.broadcast('expenses', 'add')
          }
        } catch (error) {
          console.error('Failed to add expense:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateExpense: async (id, updates) => {
        set({ isLoading: true })
        try {
          const response = await apiService.put<{ success: boolean, data: Expense }>(`/expenses/${id}`, updates)
          if (response.success) {
            set((state) => ({
              expenses: state.expenses.map(e =>
                (e.id === id || e._id === id) ? { ...e, ...response.data } : e
              )
            }))
            liveSyncService.broadcast('expenses', 'update')
          }
        } catch (error) {
          console.error('Failed to update expense:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      deleteExpense: async (id) => {
        set({ isLoading: true })
        try {
          const response = await apiService.delete<{ success: boolean }>(`/expenses/${id}`)
          if (response.success) {
            set((state) => ({
              expenses: state.expenses.filter(e =>
                (e.id !== id && e._id !== id)
              )
            }))
            liveSyncService.broadcast('expenses', 'delete')
          }
        } catch (error) {
          console.error('Failed to delete expense:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      // Cheque CRUD
      fetchCheques: async () => {
        set({ isLoading: true })
        try {
          const response = await apiService.get<{ success: boolean, data: Cheque[] }>('/cheques')
          if (response.success) {
            set({ cheques: response.data })
          }
        } catch (error) {
          console.error('Failed to fetch cheques:', error)
        } finally {
          set({ isLoading: false })
        }
      },

      addCheque: async (chequeData) => {
        set({ isLoading: true })
        try {
          const response = await apiService.post<{ success: boolean, data: Cheque }>('/cheques', chequeData)
          if (response.success) {
            set((state) => ({ cheques: [response.data, ...state.cheques] }))
          }
        } catch (error) {
          console.error('Failed to add cheque:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateCheque: async (id, updates) => {
        set({ isLoading: true })
        try {
          const response = await apiService.put<{ success: boolean, data: Cheque }>(`/cheques/${id}`, updates)
          if (response.success) {
            set((state) => ({
              cheques: state.cheques.map(c =>
                (c.id === id || c._id === id) ? { ...c, ...response.data } : c
              )
            }))
          }
        } catch (error) {
          console.error('Failed to update cheque:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      deleteCheque: async (id) => {
        set({ isLoading: true })
        try {
          const response = await apiService.delete<{ success: boolean }>(`/cheques/${id}`)
          if (response.success) {
            set((state) => ({
              cheques: state.cheques.filter(c =>
                (c.id !== id && c._id !== id)
              )
            }))
          }
        } catch (error) {
          console.error('Failed to delete cheque:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateChequeStatus: async (id, status) => {
        try {
          const response = await apiService.patch<{ success: boolean, data: Cheque }>(`/cheques/${id}/status`, { status })
          if (response.success) {
            set((state) => ({
              cheques: state.cheques.map(c =>
                (c.id === id || c._id === id) ? { ...c, ...response.data } : c
              )
            }))
          }
        } catch (error) {
          console.error('Failed to update cheque status:', error)
          throw error
        }
      },

      // Batch CRUD
      // Load stock batches via REST (kept live by Socket.IO batches:changed events).
      fetchBatches: async (_productId?: string) => {
        try {
          const body: any = await apiService.get(`/batches`)
          const list = Array.isArray(body) ? body : (body?.data || body?.batches || [])
          set({ batches: list as StockBatch[] })
          liveSyncService.markFetched('batches' as any)
        } catch (error) {
          console.error('[RT] fetchBatches failed:', error)
        }
      },

      fetchActiveBatchesForProduct: async (productId: string) => {
        try {
          const response = await apiService.get<{ success: boolean, data: StockBatch[] }>(`/batches/product/${encodeURIComponent(productId)}`)
          if (response.success) {
            return response.data
          }
          return []
        } catch (error) {
          console.error('Failed to fetch active batches:', error)
          return []
        }
      },

      addBatch: async (batchData) => {
        try {
          const response = await apiService.post<{ success: boolean, data: StockBatch }>('/batches', batchData)
          if (response.success) {
            set((state) => ({ batches: [response.data, ...state.batches] }))
            // Products/batches updated by Firestore listener automatically
          }
        } catch (error) {
          console.error('Failed to add batch:', error)
          throw error
        }
      },

      updateBatch: async (id, updates) => {
        try {
          const response = await apiService.put<{ success: boolean, data: StockBatch }>(`/batches/${id}`, updates)
          if (response.success) {
            set((state) => ({
              batches: state.batches.map(b => (b.id === id || b._id === id) ? { ...b, ...updates, ...response.data } : b)
            }))
            // Products/batches updated by Firestore listener automatically
          }
        } catch (error) {
          console.error('Failed to update batch:', error)
          throw error
        }
      },

      deleteBatch: async (id) => {
        try {
          const response = await apiService.delete<{ success: boolean }>(`/batches/${id}`)
          if (response.success) {
            set((state) => ({
              batches: state.batches.filter(b => b.id !== id && b._id !== id)
            }))
            // Products/batches updated by Firestore listener automatically
          }
        } catch (error) {
          console.error('Failed to delete batch:', error)
          throw error
        }
      },

      confirmBatchSelection: (product: Product, batch: StockBatch) => {
        const { cart, selectedCustomer, customers } = get()
        const pid = product.id || product._id

        // Resolve customer type for pricing
        const customer = selectedCustomer ? customers.find(c => (c.id || c._id) === selectedCustomer) : null
        const customerType = customer?.customerType as 'wholesale' | 'regular' | 'special' | null | undefined

        // UoM virtual card: use salesUnit price instead of raw batch price
        const salesUnit = (product as any)._salesUnit as SalesUnit | undefined
        const factor = salesUnit?.conversionFactor || 1

        // Check if already in cart (same product AND same batch)
        const existingItem = cart.find(item =>
          (item.product.id || item.product._id) === pid && item.batchId === batch.id
        )

        // Stock limit in sales-unit quantities
        const availableBaseStock = batch.currentStock || 0
        const availableStock = factor > 1 ? Math.floor(availableBaseStock / factor) : availableBaseStock
        const currentQtyInCart = existingItem ? existingItem.quantity : 0

        // Enforce stock limit: cannot exceed batch's available stock
        if (currentQtyInCart >= availableStock) {
          set({ pendingBatchSelection: null })
          return // Already at max for this batch
        }

        if (existingItem) {
          set({
            cart: cart.map(item =>
              (item.product.id || item.product._id) === pid && item.batchId === batch.id
                ? { ...item, quantity: Math.min(item.quantity + 1, availableStock) }
                : item
            )
          })
        } else {
          // For UoM virtual cards, use the salesUnit-specific price; for regular products, use batch price
          let unitPrice: number
          if (salesUnit) {
            unitPrice = customerType === 'wholesale' ? (salesUnit.wholesalePrice ?? salesUnit.price)
              : (customerType === 'regular' || customerType === 'special') ? (salesUnit.discountPrice ?? salesUnit.price)
              : salesUnit.price
          } else {
            unitPrice = getBatchPriceForCustomer(batch, customerType)
          }
          const priceTier: PriceTierKey = customerType === 'wholesale' ? 'wholesale'
            : (customerType === 'regular' || customerType === 'special') ? 'discount'
            : 'retail'
          const newItem: CartItem = {
            product,
            quantity: 1,
            selectedPriceTier: priceTier,
            unitPriceOverride: unitPrice,
            batchId: batch.id,
            batchLabel: batch.batchNumber,
            salesUnitId: salesUnit?.id,
            salesUnitName: salesUnit?.name,
            salesUnitFactor: salesUnit ? factor : undefined,
          }
          set({ cart: [...cart, newItem] })
        }

        set({ pendingBatchSelection: null })
        get().calculateTotals()
        get().checkStockAlerts()
      },

      cancelBatchSelection: () => {
        set({ pendingBatchSelection: null })
      },

      confirmVariantSelection: (selections: { product: Product; quantity: number }[]) => {
        set({ pendingVariantSelection: null })

        // If a single selection has salesUnits (UoM), show a unit picker instead of adding directly
        if (selections.length === 1 && selections[0].product.salesUnits && selections[0].product.salesUnits.length > 0) {
          const { products } = get()
          const pid = selections[0].product.id || selections[0].product._id
          const uomCards = products.filter(p =>
            (p as any)._masterProductId === pid && (p as any)._salesUnit
          )
          if (uomCards.length > 0) {
            set({
              pendingVariantSelection: {
                groupId: '_uom_' + pid,
                groupName: selections[0].product.name + ' — Select Unit',
                variants: uomCards,
              }
            })
            return
          }
        }

        // Add all selections to cart
        for (const { product, quantity } of selections) {
          const productWithoutGroup = { ...product, variantGroupId: undefined, variantGroupName: product.variantGroupName }
          const qty = quantity > 0 ? quantity : 1
          for (let i = 0; i < qty; i++) {
            get().addToCart(productWithoutGroup)
          }
        }
      },

      cancelVariantSelection: () => {
        set({ pendingVariantSelection: null })
      },

      addReturnToCart: (item) => {
        const { cart } = get()

        // Prevent adding the same return item twice (same product + batch + source transaction)
        const isDuplicate = cart.some(c =>
          c.isReturn &&
          (c.product.id || c.product._id) === (item.product.id || item.product._id) &&
          c.batchId === item.batchId &&
          c.returnFromTransactionId === item.transactionId
        )
        if (isDuplicate) return

        const newItem: CartItem = {
          product: item.product,
          quantity: item.quantity,
          // Store original quantity as the upper cap for updateCartItem
          originalQuantity: item.quantity,
          selectedPriceTier: 'retail',
          unitPriceOverride: item.unitPrice,
          batchId: item.batchId,
          batchLabel: item.batchLabel,
          isReturn: true,
          returnFromTransactionId: item.transactionId,
          returnFromReceiptNumber: item.receiptNumber,
        }
        set({ cart: [...cart, newItem] })
        get().calculateTotals()
      },

      addToCart: (product: Product, quantity = 1, priceTier?: PriceTierKey) => {
        // Variant group intercept — show variant picker before anything else
        // Skip for UoM virtual cards (Bundle, Printing, etc.) — they are already specific units
        if (product.variantGroupId && !(product as any)._salesUnit) {
          const { products } = get()
          // Find real variant members: master products (with UoM) + regular products (no UoM)
          // Exclude UoM virtual cards and the synthetic group card itself
          const groupVariants = products.filter(p =>
            p.variantGroupId === product.variantGroupId &&
            !(p as any)._salesUnit &&
            !(p as any)._isVariantGroupCard
          )
          set({
            pendingVariantSelection: {
              groupId: product.variantGroupId,
              groupName: product.variantGroupName || 'Select Variant',
              variants: groupVariants,
            }
          })
          return
        }

        const { cart, batches } = get()
        const pid = product.id || product._id

        // For UoM virtual cards, resolve the master product ID for batch lookups
        const salesUnit = (product as any)._salesUnit
        const masterPid = (product as any)._masterProductId || pid
        const factor = salesUnit?.conversionFactor || 1

        // Check for active batches with different selling prices (use master product ID for batch lookup)
        const productBatches = batches.filter(b => b.productId === masterPid && b.isActive && b.currentStock > 0)
        if (productBatches.length >= 2) {
          // Check if selling prices differ
          const uniquePrices = new Set(productBatches.map(b => b.sellingPrice))
          if (uniquePrices.size > 1) {
            // Multiple batches with different prices - trigger popup
            set({ pendingBatchSelection: { product, batches: productBatches } })
            return
          }
          // Same prices - consolidate stock across all batches, use oldest as primary cart entry
          if (productBatches.length > 0) {
            const oldestBatch = productBatches[0] // Already sorted by receivedDate in API
            // Consolidate base-unit stock from ALL same-price batches
            const consolidatedBaseStock = productBatches.reduce((sum, b) => sum + (b.currentStock || 0), 0)
            // In sales-unit quantities (for comparison with cart quantities)
            const consolidatedStock = factor > 1 ? Math.floor(consolidatedBaseStock / factor) : consolidatedBaseStock
            // Total quantity already in cart for this product (match by virtual pid for UoM cards)
            const totalInCart = cart
              .filter(item => (item.product.id || item.product._id) === pid && !item.isReturn)
              .reduce((sum, item) => sum + item.quantity, 0)
            if (totalInCart >= consolidatedStock) return // At max consolidated stock

            const existingItem = cart.find(item =>
              (item.product.id || item.product._id) === pid && item.batchId === oldestBatch.id && !item.isReturn
            )
            const currentQtyInCart = existingItem ? existingItem.quantity : 0
            const maxAllowed = consolidatedStock - totalInCart + currentQtyInCart
            const newQty = Math.min(currentQtyInCart + quantity, maxAllowed)
            if (existingItem) {
              set({
                cart: cart.map(item =>
                  (item.product.id || item.product._id) === pid && item.batchId === oldestBatch.id && !item.isReturn
                    ? { ...item, quantity: newQty }
                    : item
                )
              })
            } else {
              const { selectedCustomer, customers } = get()
            const batchCustomer = selectedCustomer ? customers.find((c: any) => (c.id || c._id) === selectedCustomer) : null
            const batchCustomerType = (batchCustomer as any)?.customerType || null
            const newItem: CartItem = {
                product, quantity: Math.min(quantity, consolidatedStock),
                selectedPriceTier: 'retail',
                unitPriceOverride: salesUnit
                  ? (batchCustomerType === 'wholesale' ? (salesUnit.wholesalePrice ?? salesUnit.price) : (batchCustomerType === 'regular' || batchCustomerType === 'special') ? (salesUnit.discountPrice ?? salesUnit.price) : salesUnit.price)
                  : getBatchPriceForCustomer(oldestBatch, batchCustomerType),
                batchId: oldestBatch.id,
                batchLabel: oldestBatch.batchNumber,
                salesUnitId: salesUnit?.id,
                salesUnitName: salesUnit?.name,
                salesUnitFactor: salesUnit ? factor : undefined,
              }
              set({ cart: [...cart, newItem] })
            }
            get().calculateTotals()
            get().checkStockAlerts()
            return
          }
        } else if (productBatches.length === 1) {
          // Single batch - auto-select it
          const batch = productBatches[0]
          const existingItem = cart.find(item =>
            (item.product.id || item.product._id) === pid && item.batchId === batch.id && !item.isReturn
          )
          const currentQtyInCart = existingItem ? existingItem.quantity : 0
          // Available stock in sales-unit quantities
          const availableBaseStock = batch.currentStock || 0
          const availableStock = factor > 1 ? Math.floor(availableBaseStock / factor) : availableBaseStock
          if (currentQtyInCart >= availableStock) return // At max for this batch

          const newQty = Math.min(currentQtyInCart + quantity, availableStock)
          if (existingItem) {
            set({
              cart: cart.map(item =>
                (item.product.id || item.product._id) === pid && item.batchId === batch.id && !item.isReturn
                  ? { ...item, quantity: newQty }
                  : item
              )
            })
          } else {
            const { selectedCustomer, customers } = get()
            const singleBatchCustomer = selectedCustomer ? customers.find((c: any) => (c.id || c._id) === selectedCustomer) : null
            const singleBatchCustomerType = (singleBatchCustomer as any)?.customerType || null
            const newItem: CartItem = {
              product, quantity: Math.min(quantity, availableStock),
              selectedPriceTier: 'retail',
              unitPriceOverride: salesUnit
                ? (singleBatchCustomerType === 'wholesale' ? (salesUnit.wholesalePrice ?? salesUnit.price) : (singleBatchCustomerType === 'regular' || singleBatchCustomerType === 'special') ? (salesUnit.discountPrice ?? salesUnit.price) : salesUnit.price)
                : getBatchPriceForCustomer(batch, singleBatchCustomerType),
              batchId: batch.id,
              batchLabel: batch.batchNumber,
              salesUnitId: salesUnit?.id,
              salesUnitName: salesUnit?.name,
              salesUnitFactor: salesUnit ? factor : undefined,
            }
            set({ cart: [...cart, newItem] })
          }
          get().calculateTotals()
          get().checkStockAlerts()
          return
        }

        // No batches - legacy behavior (use product.stock as limit)
        // product.stock is already in sales-unit quantities for UoM virtual cards
        const existingItem = cart.find(item => (item.product.id || item.product._id) === pid && !item.batchId && !item.isReturn)
        const currentQtyInCart = existingItem ? existingItem.quantity : 0
        const availableStock = product.stock || 0
        if (availableStock > 0 && currentQtyInCart >= availableStock) return // At max stock

        const cappedQty = availableStock > 0
          ? Math.min(currentQtyInCart + quantity, availableStock)
          : currentQtyInCart + quantity

        if (existingItem) {
          set({
            cart: cart.map(item =>
              (item.product.id || item.product._id) === pid && !item.batchId && !item.isReturn
                ? { ...item, quantity: cappedQty }
                : item
            )
          })
        } else {
          // Apply customer-aware pricing: wholesale → wholesale price, regular → discount, guest → retail
          const { selectedCustomer, customers } = get()
          const customer = selectedCustomer ? customers.find((c: any) => (c.id || c._id) === selectedCustomer) : null
          const customerType = (customer as any)?.customerType || null
          const tier = priceTier || getDefaultPriceTier(product, customerType)
          const addQty = availableStock > 0 ? Math.min(quantity, availableStock) : quantity
          const newItem: CartItem = {
            product, quantity: addQty, selectedPriceTier: tier,
            salesUnitId: salesUnit?.id,
            salesUnitName: salesUnit?.name,
            salesUnitFactor: salesUnit ? factor : undefined,
          }
          // Pre-compute the effective price for display
          newItem.unitPriceOverride = getEffectivePrice(newItem)
          set({
            cart: [...cart, newItem]
          })
        }

        get().calculateTotals()
        get().checkStockAlerts()
      },

      removeFromCart: (productId: string, batchId?: string) => {
        const { cart } = get()
        set({
          cart: cart.filter(item => {
            const pid = item.product.id || item.product._id
            if (pid !== productId) return true
            // If batchId is specified, only remove the matching batch entry
            if (batchId !== undefined) return item.batchId !== batchId
            // No batchId specified — only remove items without a batchId (non-batch items)
            return item.batchId !== undefined
          })
        })
        get().calculateTotals()
        get().checkStockAlerts()
      },

      updateCartItem: (productId: string, quantity: number, batchId?: string) => {
        if (quantity <= 0) {
          get().removeFromCart(productId, batchId)
          return
        }

        const { cart, batches } = get()

        // Get the cart item to check if it's a UoM item
        const cartItemRef = cart.find(item => {
          const pid = item.product.id || item.product._id
          if (pid !== productId) return false
          if (batchId !== undefined) return item.batchId === batchId
          return item.batchId === undefined
        })

        // Return items: cap at the original sale quantity (can't return more than was bought)
        if (cartItemRef?.isReturn) {
          const maxReturn = cartItemRef.originalQuantity ?? cartItemRef.quantity
          const cappedQty = Math.min(quantity, maxReturn)
          set({
            cart: cart.map(item => {
              const pid = item.product.id || item.product._id
              if (pid !== productId) return item
              if (batchId !== undefined) return item.batchId === batchId ? { ...item, quantity: cappedQty } : item
              return item.batchId === undefined ? { ...item, quantity: cappedQty } : item
            })
          })
          get().calculateTotals()
          return
        }

        const suFactor = cartItemRef?.salesUnitFactor || 1

        // Determine max allowed quantity based on batch or product stock
        let maxStock = Infinity
        if (batchId) {
          const batch = batches.find(b => b.id === batchId)
          if (batch) {
            // Check if all active batches for this product have the same price (consolidated stock)
            const masterPid = (cartItemRef?.product as any)?._masterProductId || batch.productId
            const productBatches = batches.filter(b => b.productId === masterPid && b.isActive && b.currentStock > 0)
            const uniquePrices = new Set(productBatches.map(b => b.sellingPrice))
            const baseStock = uniquePrices.size <= 1 && productBatches.length > 1
              ? productBatches.reduce((sum, b) => sum + (b.currentStock || 0), 0)
              : (batch.currentStock || 0)
            // Convert base units to sales-unit quantities
            maxStock = suFactor > 1 ? Math.floor(baseStock / suFactor) : baseStock
          }
        } else {
          // Non-batch item — use overall product stock (already in sales-unit quantities for UoM virtual cards)
          const cartItem = cart.find(item =>
            (item.product.id || item.product._id) === productId && !item.batchId
          )
          if (cartItem) {
            const productStock = cartItem.product.stock || 0
            if (productStock > 0) maxStock = productStock
          }
        }

        const cappedQuantity = Math.min(quantity, maxStock)

        set({
          cart: cart.map(item => {
            const pid = item.product.id || item.product._id
            if (pid !== productId) return item
            // Match by batchId when specified
            if (batchId !== undefined) {
              return item.batchId === batchId ? { ...item, quantity: cappedQuantity } : item
            }
            // No batchId — only update non-batch items
            return item.batchId === undefined ? { ...item, quantity: cappedQuantity } : item
          })
        })
        get().calculateTotals()
        get().checkStockAlerts()
      },

      setCartItemPriceTier: (productId: string, priceTier: PriceTierKey, batchId?: string) => {
        const { cart, batches } = get()
        set({
          cart: cart.map(item => {
            const pid = item.product.id || item.product._id
            if (pid !== productId) return item
            // Match by batchId when specified
            if (batchId !== undefined && item.batchId !== batchId) return item
            if (batchId === undefined && item.batchId !== undefined) return item
            const updated = { ...item, selectedPriceTier: priceTier }
            // For batch items, use batch-level prices instead of product-level
            if (item.batchId) {
              const batch = batches.find(b => b.id === item.batchId)
              if (batch) {
                const customerType = priceTier === 'wholesale' ? 'wholesale'
                  : priceTier === 'discount' ? 'regular'
                  : null
                updated.unitPriceOverride = getBatchPriceForCustomer(batch, customerType)
              } else {
                updated.unitPriceOverride = getEffectivePrice(updated)
              }
            } else {
              updated.unitPriceOverride = getEffectivePrice(updated)
            }
            return updated
          })
        })
        get().calculateTotals()
      },

      setCartItemDiscount: (productId: string, discountPercent: number, batchId?: string) => {
        const { cart } = get()
        const value = Math.max(0, Math.min(100, Number(discountPercent) || 0))
        set({
          cart: cart.map(item => {
            if (item.isReturn) return item
            const pid = item.product.id || item.product._id
            if (pid !== productId) return item
            if (batchId !== undefined && item.batchId !== batchId) return item
            if (batchId === undefined && item.batchId !== undefined) return item
            return { ...item, discount: value }
          })
        })
        get().calculateTotals()
      },

      clearCart: () => {
        set({
          cart: [],
          selectedCustomer: null,
          selectedCustomerName: null,
          discount: 0,
          tax: 0,
          subtotal: 0,
          total: 0,
          paymentSplits: [],
          stockAlerts: []
        })
      },

      setSelectedCustomer: (customerId: string | null, customerName?: string | null) => {
        set({ selectedCustomer: customerId, selectedCustomerName: customerName || null })
        // Re-apply customer-aware price tiers to all non-batch, non-return cart items
        const { cart, customers } = get()
        const customer = customerId ? customers.find((c: any) => (c.id || c._id) === customerId) : null
        const customerType = (customer as any)?.customerType || null
        const updatedCart = cart.map(item => {
          if (item.isReturn) return item
          const salesUnit = (item.product as any)._salesUnit
          // Batch items: reprice using the batch's own tier prices
          if (item.batchId) {
            const matchedBatch = get().batches.find(b => b.id === item.batchId)
            if (matchedBatch) {
              const newPrice = salesUnit
                ? (customerType === 'wholesale' ? (salesUnit.wholesalePrice ?? salesUnit.price) : (customerType === 'regular' || customerType === 'special') ? (salesUnit.discountPrice ?? salesUnit.price) : salesUnit.price)
                : getBatchPriceForCustomer(matchedBatch, customerType)
              return { ...item, unitPriceOverride: newPrice }
            }
            return item
          }
          // UoM sales-unit cards (no batch): recalculate from the sales unit's own price fields
          if (salesUnit) {
            const newPrice = customerType === 'wholesale'
              ? (salesUnit.wholesalePrice ?? salesUnit.price)
              : (customerType === 'regular' || customerType === 'special')
                ? (salesUnit.discountPrice ?? salesUnit.price)
                : salesUnit.price
            return { ...item, unitPriceOverride: newPrice }
          }
          // Standard items without batch: update tier and clear override so tier drives price
          const newTier = getDefaultPriceTier(item.product, customerType)
          return { ...item, selectedPriceTier: newTier, unitPriceOverride: undefined }
        })
        set({ cart: updatedCart })
        get().calculateTotals()
      },

      setPaymentMethod: (method: PaymentMethod) => {
        set({ paymentMethod: method })
      },

      setPaymentSplits: (splits: PaymentSplit[]) => {
        set({ paymentSplits: splits })
      },

      setDiscount: (discount: number, type: 'percentage' | 'fixed' = 'percentage') => {
        if (type === 'percentage') {
          set({ discount: Math.max(0, Math.min(100, discount)), discountType: 'percentage' })
        } else {
          // Cap fixed discount to current subtotal to prevent negative totals
          const { cart } = get()
          const purchaseSubtotal = cart.filter(i => !i.isReturn).reduce((sum, item) => sum + getLineTotalForItem(item), 0)
          set({ discount: Math.max(0, Math.min(discount, purchaseSubtotal)), discountType: 'fixed' })
        }
        get().calculateTotals()
      },

      setTax: (tax: number) => {
        set({ tax: Math.max(0, tax) })
        get().calculateTotals()
      },

      calculateTotals: () => {
        const { cart, discount, discountType } = get()

        if (cart.length === 0) {
          set({ subtotal: 0, total: 0 })
          return
        }

        // Separate purchases and returns for correct discount/tax math
        const purchaseSubtotal = cart.filter(i => !i.isReturn).reduce((sum, item) => sum + getLineTotalForItem(item), 0)
        const returnCredit = cart.filter(i => i.isReturn).reduce((sum, item) => sum + getLineTotalForItem(item), 0)

        // The displayed subtotal is purchases - returns (for backward compat)
        const subtotal = purchaseSubtotal - returnCredit

        // Discount applies only to purchases, NOT offset by returns
        const discountAmount = discountType === 'percentage'
          ? (purchaseSubtotal * discount) / 100
          : Math.min(discount, purchaseSubtotal)

        // No tax system — total = purchases - discount - returns
        const total = purchaseSubtotal - discountAmount - returnCredit

        set({
          subtotal,
          total: Math.round(total * 100) / 100
        })
      },

      processTransaction: async (amountPaid?: number, change?: number, advanceReceiptsToApply?: Array<{ requestId: string; amount: number }>) => {
        const { cart, batches, selectedCustomer, selectedCustomerName, paymentMethod, paymentSplits, discount, discountType, tax, total, customers } = get()
        const applied = (advanceReceiptsToApply ?? []).reduce((sum, e) => sum + e.amount, 0)
        const balanceDue = total - applied
        const effectiveAmountPaid = amountPaid != null ? amountPaid : balanceDue

        // Validate cart has items
        if (cart.length === 0) {
          throw new Error('Cart is empty')
        }

        // Validate amountPaid
        if (!Number.isFinite(effectiveAmountPaid) || effectiveAmountPaid < 0) {
          throw new Error('Invalid payment amount')
        }

        // Validate split payments sum to balance due
        if (paymentMethod === 'split' && paymentSplits.length > 0) {
          const splitsTotal = paymentSplits.reduce((sum, s) => sum + s.amount, 0)
          if (Math.abs(splitsTotal - balanceDue) > 1) {
            throw new Error(`Split payments (Rs.${splitsTotal.toFixed(2)}) don't match balance due (Rs.${balanceDue.toFixed(2)})`)
          }
        }

        // Validate discount bounds
        if (discountType === 'percentage' && (discount < 0 || discount > 100)) {
          throw new Error('Invalid discount percentage')
        }

        // Validate wallet balance before attempting payment
        if (selectedCustomer && (paymentMethod === 'wallet' || paymentMethod === 'split')) {
          const customer = customers.find(c => (c.id || c._id) === selectedCustomer)
          if (customer) {
            const creditLimit = customer.creditLimit || 0
            const lowestAllowed = customer.customerType === 'special' ? -creditLimit : 0
            const currentBalance = customer.walletBalance || 0

            let walletAmount = 0
            if (paymentMethod === 'wallet') {
              walletAmount = balanceDue
            } else {
              const walletSplit = paymentSplits.find(s => (s.method as string) === 'wallet')
              walletAmount = walletSplit ? walletSplit.amount : 0
            }

            if (walletAmount > 0 && (currentBalance - walletAmount) < lowestAllowed) {
              const maxUsable = currentBalance - lowestAllowed
              throw new Error(
                customer.customerType === 'special'
                  ? `Exceeds credit limit. Max wallet usable: Rs.${maxUsable.toFixed(2)}`
                  : `Insufficient wallet balance (Rs.${currentBalance.toFixed(2)}). Required: Rs.${walletAmount.toFixed(2)}`
              )
            }
          }
        }

        // Get logged-in user from localStorage for employee ID
        let employeeId = 'unknown'
        try {
          const userData = localStorage.getItem('user')
          if (userData) {
            const user = JSON.parse(userData)
            employeeId = user.id || user._id || 'unknown'
          }
        } catch {
          // ignore parse errors
        }

        const customerName = selectedCustomerName ||
          (selectedCustomer ? customers.find(c => (c.id || c._id) === selectedCustomer)?.name : null) ||
          'Guest'

        const transaction: Transaction = {
          id: `txn_${Date.now()}`,
          idempotencyKey: `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
          transactionNumber: `TRX-${Date.now()}`,
          receiptNumber: `RCP-${Date.now()}`,
          items: cart,
          customerId: selectedCustomer || undefined,
          customerName,
          employeeId,
          paymentMethod,
          paymentSplits: paymentMethod === 'split' ? paymentSplits : undefined,
          discount,
          tax,
          subtotal: get().subtotal,
          total,
          amountPaid: effectiveAmountPaid,
          advanceApplied: applied,
          change,
          status: 'completed',
          createdAt: new Date(),
          updatedAt: new Date(),
        }

        // Process online transaction
        try {
          // Build flattened item payloads, splitting consolidated batch items FIFO across batches
          const itemPayloads: any[] = []
          for (const item of cart) {
            const unitPrice = getEffectivePrice(item)
            // For UoM items, real productId is _masterProductId; quantity is in sales units
            const realProductId = (item.product as any)._masterProductId || item.product.id || (item.product as any)._id
            const suFactor = item.salesUnitFactor || 1
            // Quantity sent to backend is always in base units
            const baseQuantity = Math.round(item.quantity * suFactor)

            const buildPayload = (qty: number, bId?: string, bLabel?: string) => {
              // qty here is in BASE units
              const displayQty = suFactor > 1 ? Math.round(qty / suFactor) : qty
              const lineTotal = unitPrice * displayQty * (1 - (item.discount || 0) / 100)
              const payload: any = {
                productId: realProductId,
                productName: item.product.name,
                variantGroupName: item.product.variantGroupName || null,
                price: unitPrice,
                sku: item.product.sku,
                quantity: qty,          // base units — backend uses this for stock deduction
                displayQuantity: displayQty, // sales-unit qty — for receipt display
                discount: item.discount || 0,
                subtotal: item.isReturn ? -lineTotal : lineTotal,
                total: item.isReturn ? -lineTotal : lineTotal,
                priceTier: item.selectedPriceTier || 'retail',
              }
              if (item.salesUnitId) {
                payload.salesUnitId = item.salesUnitId
                payload.salesUnitName = item.salesUnitName
                payload.salesUnitFactor = suFactor
              }
              if (bId) { payload.batchId = bId; payload.batchLabel = bLabel }
              if (item.isReturn) {
                payload.isReturn = true
                payload.returnFromTransactionId = item.returnFromTransactionId
                payload.returnFromReceiptNumber = item.returnFromReceiptNumber
              }
              return payload
            }

            if (item.batchId && !item.isReturn) {
              // Check if this product has same-price consolidated batches (use real/master product ID)
              const productBatches = batches
                .filter(b => b.productId === realProductId && b.isActive && b.currentStock > 0)
                .sort((a, b) => new Date(a.receivedDate as any).getTime() - new Date(b.receivedDate as any).getTime())
              const uniquePrices = new Set(productBatches.map(b => b.sellingPrice))

              if (uniquePrices.size <= 1 && productBatches.length > 1) {
                // Split across batches FIFO (in base units)
                let remaining = baseQuantity
                for (const batch of productBatches) {
                  if (remaining <= 0) break
                  const take = Math.min(remaining, batch.currentStock || 0)
                  if (take > 0) {
                    itemPayloads.push(buildPayload(take, batch.id, batch.batchNumber))
                    remaining -= take
                  }
                }
                // If any remaining after FIFO split, allocate to the original batch as fallback
                if (remaining > 0) {
                  console.warn(`FIFO split: ${remaining} units unallocated for ${item.product.name}, falling back to batch ${item.batchId}`)
                  itemPayloads.push(buildPayload(remaining, item.batchId, item.batchLabel))
                }
              } else {
                itemPayloads.push(buildPayload(baseQuantity, item.batchId, item.batchLabel))
              }
            } else {
              itemPayloads.push(buildPayload(baseQuantity, item.batchId, item.batchLabel))
            }
          }

          const response = await apiService.post<{ success: boolean; data: Transaction }>('/transactions', {
            idempotencyKey: transaction.idempotencyKey,
            items: itemPayloads,
            customerId: selectedCustomer,
            paymentMethod,
            paymentSplits: paymentMethod === 'split' ? paymentSplits : undefined,
            discount,
            discountType,
            tax,
            subtotal: get().subtotal,
            total,
            amountPaid: effectiveAmountPaid,
            advanceApplied: applied,
            change,
          })

          if (advanceReceiptsToApply?.length) {
            get().applyAdvanceByRequestIds(advanceReceiptsToApply)
          }

          const completedTransaction = {
            ...transaction,
            ...response.data,
          }

          set({ lastTransaction: completedTransaction })

          // Clear cart and reset for next transaction
          get().clearCart()
          set({
            selectedCustomer: null,
            selectedCustomerName: null,
            discount: 0,
            discountType: 'percentage'
          })

          // Notify other tabs about the transaction
          liveSyncService.broadcast('transactions', 'add')
          // Products/batches updated by Firestore listener automatically

          return completedTransaction
        } catch (error) {
          console.error('Transaction processing failed:', error)
          // If online processing fails, add to pending
          get().addPendingTransaction(transaction)
          set({ lastTransaction: transaction })
          throw error
        }
      },

      addPendingTransaction: (transaction: Transaction) => {
        const { pendingTransactions } = get()
        set({
          pendingTransactions: [...pendingTransactions, transaction]
        })
      },

      syncPendingTransactions: async () => {
        const { pendingTransactions } = get()
        if (pendingTransactions.length === 0) return

        const failedTransactions: Transaction[] = []

        for (const transaction of pendingTransactions) {
          try {
            // Handle both CartItem[] and flat item array formats
            const itemsPayload = transaction.items.map((item: any) => {
              // Check if item has 'product' property (CartItem format)
              if (item.product) {
                const lineTotal = getLineTotalForItem(item as CartItem)
                return {
                  productId: item.product.id || item.product._id,
                  productName: item.product.name,
                  variantGroupName: item.product.variantGroupName || null,
                  price: getEffectivePrice(item as CartItem),
                  sku: item.product.sku,
                  quantity: item.quantity,
                  discount: item.discount || 0,
                  subtotal: item.isReturn ? -lineTotal : lineTotal,
                  total: item.isReturn ? -lineTotal : lineTotal,
                }
              }
              // Flat item format - already has productId, productName, etc.
              return {
                productId: item.productId,
                productName: item.productName,
                price: item.unitPrice || item.price,
                sku: item.sku,
                quantity: item.quantity,
                discount: item.discount || 0,
                subtotal: item.subtotal,
                total: item.total,
              }
            })

            await apiService.post('/transactions', {
              idempotencyKey: transaction.idempotencyKey,
              items: itemsPayload,
              customerId: transaction.customerId,
              paymentMethod: transaction.paymentMethod,
              paymentSplits: transaction.paymentSplits,
              discount: transaction.discount,
              tax: transaction.tax,
              subtotal: transaction.subtotal,
              total: transaction.total,
              amountPaid: transaction.amountPaid,
              change: transaction.change,
            })
            console.log('Synced pending transaction:', transaction.id)
          } catch (error) {
            console.error('Failed to sync transaction:', transaction.id, error)
            failedTransactions.push(transaction)
          }
        }

        set({ pendingTransactions: failedTransactions })

        // Products/batches updated by Firestore listener automatically
      },

      setOfflineMode: (isOffline: boolean) => {
        set({ isOffline })
        if (!isOffline) {
          get().syncPendingTransactions()
        }
      },

      // Hold Transaction Actions
      holdTransaction: (name: string, notes?: string) => {
        const { cart, selectedCustomer, selectedCustomerName, discount, discountType, tax, paymentMethod, paymentSplits, customers } = get()

        if (cart.length === 0) return

        let heldBy = 'unknown'
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}')
          heldBy = user.name || user.email || user.id || 'unknown'
        } catch { /* ignore */ }

        const customerName = selectedCustomerName ||
          (selectedCustomer ? customers.find(c => (c.id || c._id) === selectedCustomer)?.name : null)

        const heldTransaction: HeldTransaction = {
          id: `hold_${Date.now()}`,
          name: name || `Order #${Date.now().toString().slice(-4)}`,
          cart: [...cart],
          customerId: selectedCustomer || undefined,
          customerName: customerName || undefined,
          discount,
          discountType,
          tax,
          paymentMethod,
          paymentSplits: paymentMethod === 'split' ? [...paymentSplits] : undefined,
          notes,
          heldAt: new Date(),
          heldBy,
        }

        set((state) => ({
          heldTransactions: [...state.heldTransactions, heldTransaction],
          cart: [],
          selectedCustomer: null,
          selectedCustomerName: null,
          discount: 0,
          discountType: 'percentage',
          tax: 0,
          paymentMethod: 'cash',
          paymentSplits: [],
          subtotal: 0,
          total: 0,
          stockAlerts: []
        }))
      },

      resumeTransaction: (heldTransaction: HeldTransaction) => {
        set({
          cart: [...heldTransaction.cart],
          selectedCustomer: heldTransaction.customerId || null,
          selectedCustomerName: heldTransaction.customerName || null,
          discount: heldTransaction.discount,
          discountType: heldTransaction.discountType || 'percentage',
          tax: heldTransaction.tax || 0,
          paymentMethod: heldTransaction.paymentMethod || 'cash',
          paymentSplits: heldTransaction.paymentSplits || [],
          heldTransactions: get().heldTransactions.filter(t => t.id !== heldTransaction.id)
        })
        get().calculateTotals()
        get().checkStockAlerts()
      },

      deleteHeldTransaction: (transactionId: string) => {
        set((state) => ({
          heldTransactions: state.heldTransactions.filter(t => t.id !== transactionId)
        }))
      },

      // Stock Alert Actions
      checkStockAlerts: () => {
        const { cart, products } = get()
        const alerts: StockAlert[] = []

        cart.forEach(item => {
          const product = products.find(p => (p.id || p._id) === (item.product.id || item.product._id)) || item.product
          const remainingStock = product.stock - item.quantity

          if (remainingStock < 0) {
            alerts.push({
              productId: product.id || product._id || '',
              productName: product.name,
              currentStock: product.stock,
              minStock: product.minStock,
              severity: 'critical'
            })
          } else if (remainingStock <= product.minStock) {
            alerts.push({
              productId: product.id || product._id || '',
              productName: product.name,
              currentStock: product.stock,
              minStock: product.minStock,
              severity: 'warning'
            })
          }
        })

        set({ stockAlerts: alerts })
      },

      dismissStockAlerts: () => {
        set({ stockAlerts: [] })
      },

      recordAdvancePayment: async (params) => {
        const { requestId, amount, totalAtRequest, customerPhone, customerName, customerId, items } = params
        if (amount <= 0) return
        // Persist to backend
        try {
          const res = await apiService.post<{ success: boolean; data: { id: string; requestId: string; amount: number; appliedAmount: number; totalAtRequest: number; createdAt: string; customerPhone?: string } }>('/advance-payments', {
            requestId,
            amount,
            totalAtRequest,
            customerPhone: customerPhone || null,
            customerName: customerName || null,
            customerId: customerId || null,
            items: items || [],
          })
          if (res.success && res.data) {
            const record = {
              id: res.data.id,
              requestId: res.data.requestId,
              customerId: customerId || null,
              amount: res.data.amount,
              appliedAmount: res.data.appliedAmount || 0,
              totalAtRequest: res.data.totalAtRequest || 0,
              createdAt: res.data.createdAt,
            }
            set((state) => ({ advancePayments: [...state.advancePayments, record] }))
            return
          }
        } catch (err) {
          console.warn('[Advance] Backend save failed, saving locally:', err)
        }
        // Fallback: save locally only
        const record = {
          id: `adv_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          requestId,
          customerId: customerId || null,
          amount,
          appliedAmount: 0,
          totalAtRequest,
          createdAt: new Date().toISOString(),
        }
        set((state) => ({ advancePayments: [...state.advancePayments, record] }))
      },

      getAdvanceByRequestId: (requestId) => {
        const normalized = requestId.trim().toUpperCase()
        const { advancePayments } = get()
        const found = advancePayments.find(
          (a) => a.requestId.toUpperCase() === normalized
        )
        return found
          ? { requestId: found.requestId, amount: found.amount, appliedAmount: found.appliedAmount }
          : null
      },

      getUnappliedAmountForRequestId: (requestId) => {
        const advance = get().getAdvanceByRequestId(requestId)
        return advance ? Math.max(0, advance.amount - advance.appliedAmount) : 0
      },

      applyAdvanceByRequestIds: (entries) => {
        if (!entries.length) return
        // Update local state immediately
        set((state) => {
          const list = [...state.advancePayments]
          for (const { requestId, amount } of entries) {
            if (amount <= 0) continue
            const idx = list.findIndex((a) => a.requestId.toUpperCase() === requestId.trim().toUpperCase())
            if (idx === -1) continue
            const available = list[idx].amount - list[idx].appliedAmount
            const apply = Math.min(available, amount)
            if (apply <= 0) continue
            list[idx] = { ...list[idx], appliedAmount: list[idx].appliedAmount + apply }
          }
          return { advancePayments: list }
        })
        // Fire-and-forget backend sync for each applied advance
        const { advancePayments } = get()
        for (const { requestId, amount } of entries) {
          if (amount <= 0) continue
          const adv = advancePayments.find((a) => a.requestId.toUpperCase() === requestId.trim().toUpperCase())
          if (!adv) continue
          apiService.patch(`/advance-payments/${adv.id}/apply`, { applyAmount: amount }).catch((err: unknown) => {
            console.warn('[Advance] Backend apply failed:', err)
          })
        }
      },

      topUpCustomerWallet: async (customerId, amount, note) => {
        const response = await apiService.post<{ success: boolean; data: { walletBalance: number } }>(
          `/customers/${customerId}/wallet/topup`,
          { amount, note }
        )
        if (response.success) {
          set((state) => ({
            customers: state.customers.map((c) =>
              (c.id || c._id) === customerId ? { ...c, walletBalance: response.data.walletBalance } : c
            )
          }))
        }
      },

      // Wallet deduction at checkout is handled server-side in pos-transactions.js

      addQuickButton: (productId) => {
        const { quickButtons, products } = get()
        if (quickButtons.some((q) => q.productId === productId)) return
        const product = products.find((p) => (p.id || p._id) === productId)
        if (!product) return
        const newButton: QuickButton = {
          id: `qb_${productId}`,
          productId,
          productName: product.name,
          position: quickButtons.length,
        }
        set({ quickButtons: [...quickButtons, newButton] })
      },

      removeQuickButton: (productId) => {
        set((state) => ({
          quickButtons: state.quickButtons
            .filter((q) => q.productId !== productId)
            .map((q, i) => ({ ...q, position: i })),
        }))
      },

      addProduct: async (productData) => {
        set({ isLoading: true })
        try {
          // Map frontend field names to backend field names
          const backendPayload: any = { ...productData }
          if ('name' in backendPayload) {
            backendPayload.title = backendPayload.name
            delete backendPayload.name
          }
          if ('price' in backendPayload) {
            backendPayload.salePrice = backendPayload.price
            delete backendPayload.price
          }
          if ('cost' in backendPayload) {
            backendPayload.originalPrice = backendPayload.cost
            delete backendPayload.cost
          }
          if (backendPayload.category && typeof backendPayload.category === 'object') {
            backendPayload.category = backendPayload.category.name || backendPayload.category.id
          }
          const response = await apiService.post<{ success: boolean, data: Product }>('/products', backendPayload)
          if (response.success) {
            set((state) => ({ products: [...state.products, response.data] }))
            liveSyncService.broadcast('products', 'add')
          }
        } catch (error) {
          console.error('Failed to add product:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateProduct: async (id, updates) => {
        set({ isLoading: true })
        try {
          // Map frontend field names back to backend field names
          const backendPayload: any = { ...updates }
          if ('name' in backendPayload) {
            backendPayload.title = backendPayload.name
            delete backendPayload.name
          }
          if ('price' in backendPayload) {
            backendPayload.salePrice = backendPayload.price
            delete backendPayload.price
          }
          if ('cost' in backendPayload) {
            backendPayload.originalPrice = backendPayload.cost
            delete backendPayload.cost
          }
          // Strip runtime-only fields that shouldn't go to backend
          delete backendPayload.id
          delete backendPayload._id
          delete backendPayload._masterProductId
          delete backendPayload._salesUnit
          delete backendPayload._isVariantGroupCard
          delete backendPayload._isVariantMember
          // Category: send as string ID/name, not full object
          if (backendPayload.category && typeof backendPayload.category === 'object') {
            backendPayload.category = backendPayload.category.name || backendPayload.category.id
          }
          const response = await apiService.put<{ success: boolean, data: Product }>(`/products/${id}`, backendPayload)
          if (response.success) {
            set((state) => ({
              products: state.products.map((p) => (p.id === id || (p as any)._id === id ? response.data : p))
            }))
            liveSyncService.broadcast('products', 'update')
          }
        } catch (error) {
          console.error('Failed to update product:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      deleteProduct: async (id) => {
        set({ isLoading: true })
        try {
          const response = await apiService.delete<{ success: boolean }>(`/products/${id}`)
          if (response.success) {
            set((state) => ({
              products: state.products.filter((p) => p.id !== id && (p as any)._id !== id)
            }))
            liveSyncService.broadcast('products', 'delete')
          }
        } catch (error) {
          console.error('Failed to delete product:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      addCategory: async (categoryData) => {
        set({ isLoading: true })
        try {
          const response = await apiService.post<{ success: boolean; data: any }>('/categories', categoryData)
          if (response.success) {
            const newCategory = mapCategory(response.data)
            set((state) => ({ categories: [newCategory, ...state.categories] }))
            liveSyncService.broadcast('categories', 'add')
          }
        } catch (error) {
          console.error('Failed to add category:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      updateCategory: async (id, updates) => {
        set({ isLoading: true })
        try {
          const response = await apiService.put<{ success: boolean; data: any }>(`/categories/${id}`, updates)
          if (response.success) {
            const updated = mapCategory(response.data)
            set((state) => ({
              categories: state.categories.map((c) => (c.id === id || (c as any)._id === id ? updated : c)),
            }))
            liveSyncService.broadcast('categories', 'update')
          }
        } catch (error) {
          console.error('Failed to update category:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      deleteCategory: async (id) => {
        set({ isLoading: true })
        try {
          const response = await apiService.delete<{ success: boolean }>(`/categories/${id}`)
          if (response.success) {
            set((state) => ({
              categories: state.categories.filter((c) => (c.id || c._id) !== id),
            }))
            liveSyncService.broadcast('categories', 'delete')
          }
        } catch (error) {
          console.error('Failed to delete category:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },
    }),
    {
      name: 'esales-pos',
      partialize: (state) => ({
        cart: state.cart,
        selectedCustomer: state.selectedCustomer,
        selectedCustomerName: state.selectedCustomerName,
        paymentMethod: state.paymentMethod,
        paymentSplits: state.paymentSplits,
        discount: state.discount,
        discountType: state.discountType,
        tax: state.tax,
        pendingTransactions: state.pendingTransactions,
        heldTransactions: state.heldTransactions,
        advancePayments: state.advancePayments,
        quickButtons: state.quickButtons,
      }),
    }
  )
)
